/**
 * How the equipment search reads what somebody types.
 *
 * The field invites a sentence — „Potrzebuję aparatu…" — so a plain substring
 * match was not enough: „aparatu" is not inside „Aparaty", and „potrzebuję" is
 * inside nothing. Three small steps close that gap without a search library:
 *
 * 1. Fold case and Polish diacritics (`ł` is not decomposed by NFD, so it is
 *    mapped by hand), so „swiatlo" finds „Światło".
 * 2. Drop the filler words a sentence brings with it.
 * 3. Cut every remaining word to its first five letters — a crude stem, but
 *    Polish inflects at the end of the word, so „mikrofonu", „aparatu" and
 *    „statywu" land on „mikro", „apara" and „staty".
 *
 * Every word left must match somewhere in the item's text (AND, not OR), so
 * „sony 24" narrows rather than widens.
 */

const FILLER = new Set([
  'potrzebuje',
  'potrzebna',
  'potrzebny',
  'szukam',
  'chce',
  'chcialbym',
  'chcialabym',
  'mi',
  'do',
  'na',
  'z',
  'i',
  'jakis',
  'jakiegos',
  'cos',
])

/** Words people use for a category that its label does not contain. */
const SYNONYMS: Record<string, string> = {
  aparaty: 'aparat bezlusterkowiec foto zdjecia',
  obiektywy: 'obiektyw szklo zoom',
  kamery: 'kamera gimbal vlog',
  swiatlo: 'lampa led oswietlenie',
  dzwiek: 'mikrofon audio glos',
  statywy: 'statyw glowica',
}

const STEM = 5

export function foldText(text: string): string {
  return text
    .toLowerCase()
    .replace(/ł/g, 'l')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
}

/** The query as the stems every item must contain; empty means "show all". */
export function queryStems(query: string): string[] {
  return foldText(query)
    .split(' ')
    .filter((word) => word && !FILLER.has(word))
    .map((word) => (word.length > STEM ? word.slice(0, STEM) : word))
}

export function matchesStems(
  item: { name: string; blurb: string; categoryLabel: string },
  stems: string[],
): boolean {
  if (stems.length === 0) return true
  const label = foldText(item.categoryLabel)
  const haystack = `${foldText(`${item.name} ${item.blurb}`)} ${label} ${SYNONYMS[label] ?? ''}`
  return stems.every((stem) => haystack.includes(stem))
}
